import { styled } from "@mui/material/styles";
import { DreamTableFilterComponent, DreamTableMakeFilter } from "../dreamTableTypes";
import { describeEnumSelection } from "./filterValueStatus";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { kebabCase } from "@portal/app/_common/lib/changeCase";

export const EnumMultiSelectChipFilter = <T extends string>({
  id,
  label,
  value = [],
  setValue,
  enumToFriendlyMap,
  optionsToDisable,
  disabledTooltipTitle = "",
}: Parameters<DreamTableFilterComponent>[0] & {
  value: T[];
  setValue: (newValue: T[], changedValue?: T) => void;
  enumToFriendlyMap: Record<string, string>;
  optionsToDisable?: string[];
  disabledTooltipTitle?: React.ReactNode;
}) => {
  const enumEntries = Object.entries(enumToFriendlyMap);

  const handleSelection = (option: string) => {
    const currentValues = value || [];
    let newValues: string[];
    // If already selected, remove it
    if (currentValues.includes(option)) {
      newValues = currentValues.filter((item: string) => item !== option);
    }
    // If not selected, add it
    else {
      newValues = [...currentValues, option];
    }
    // Nothing selected has to be handled as `[]` / no filter
    setValue(newValues.length ? newValues : [], option);
  };

  return (
    <FilterContainer>
      <Header>{label}</Header>
      <ChipRow>
        {enumEntries.map(([key, friendlyKey]) => {
          const disabled = optionsToDisable?.includes(key);

          return (
            <Tooltip key={key} arrow title={disabled ? disabledTooltipTitle : ""}>
              <StyledChip
                key={key}
                label={friendlyKey}
                disabled={disabled}
                className={value?.includes(key) ? "selected" : ""}
                onClick={() => handleSelection(key)}
                data-testid={kebabCase(`${id}-option-${friendlyKey}`)}
              />
            </Tooltip>
          );
        })}
      </ChipRow>
    </FilterContainer>
  );
};

export const makeDreamTableEnumMultiSelectChipFilter = (
  enumToFriendlyMap: Record<string, string>
): DreamTableMakeFilter => ({
  FilterComponent: (props) => EnumMultiSelectChipFilter({ enumToFriendlyMap, ...props }),
  describeValue: (value) => describeEnumSelection(enumToFriendlyMap, value),
});

const StyledChip = styled(Chip)`
  background-color: ${({ theme }) => theme.palette.dream.grayLight};
  color: ${({ theme }) => theme.palette.text.primary};

  &:hover {
    background-color: ${({ theme }) => theme.palette.dream.grayMedium};
  }

  &.selected {
    background-color: ${({ theme }) => theme.palette.primary.dark};
    color: ${({ theme }) => theme.palette.common.white};

    &:hover {
      background-color: ${({ theme }) => theme.palette.primary.dark};
      opacity: 0.9;
    }
  }
`;

const Header = styled(Typography)`
  font-weight: 700;
  font-size: 1.25rem;
`;

const FilterContainer = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ChipRow = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;
